import React, { useState, useEffect, useImperativeHandle, Dispatch, SetStateAction, forwardRef } from "react";
import { Grid } from "@mui/material";
import { StriimTypography, StriimCodeBlock } from "@striim/striim-ui";

import CircularProgress from "@mui/material/CircularProgress";

import { styles } from "./validation-list.styles";
import EllipsisText from "src/generic/ellipsis-text/ellipsis-text";
import WarningIcon from "src/generic/icon/wizard-icon/warning.svg";
import SuccessIcon from "src/generic/icon/wizard-icon/successOutlined.svg";
import ErrorIcon from "src/generic/icon/wizard-icon/errorFilled.svg";

export interface ValidationListProperty {
    id: string;
    status: string;
    text: string;
    message?: string;
    children?: object;
    method: any;
}

interface ValidationListProps {
    validationList: ValidationListProperty[];
    left?: boolean;
    setAPIStatus: Dispatch<SetStateAction<APIStatus>>;
    componentRef?: any;
    cancelRef?: any;
}

export interface ValidationListRef {
    retryAPI: () => void;
}

export const ValidationList = forwardRef<ValidationListRef, ValidationListProps>(
    ({ validationList, left = true, setAPIStatus, cancelRef }: ValidationListProps, ref) => {
        const [validationListState, setValidationListState] = useState(validationList);

        useEffect(() => {
            executeValidationListAPI();
        }, []);

        useImperativeHandle<ValidationListRef, ValidationListRef>(ref, () => ({
            retryAPI
        }));

        const retryAPI = () => {
            setValidationListState(validationList);
            executeValidationListAPI();
        };

        const updateStatus = async (list, index) => {
            let hasFailure,
                hasWarning,
                failedStep = false;
            const listItem = list[index];

            if (!listItem) {
                return;
            }
            const hasChildValidationList = ["Validating Permissions", "Validating CDC Permissions"];
            try {
                let response;
                if (listItem.id === "fetch") {
                    response = await listItem.method();
                } else if (listItem.id === "validate") {
                    response = await listItem.method();
                }
                if (response === "Completed." || response === "Connection Successful!")
                    response = JSON.stringify({ result: true });
                const parsedResponse = typeof response === "string" ? JSON.parse(response) : response;
                if (parsedResponse) {
                    if (parsedResponse?.viewDocumentation) {
                        listItem.viewDocumentation = parsedResponse.viewDocumentation;
                    }
                    if (parsedResponse?.result || parsedResponse === true) {
                        listItem.status = "success";
                    } else if (parsedResponse.result === false) {
                        failedStep = true;
                    } else if (hasChildValidationList.includes(listItem?.text)) {
                        if (Array.isArray(parsedResponse)) {
                            hasFailure = parsedResponse.some(childList => {
                                return childList.status === "failed";
                            });
                            hasWarning = parsedResponse.some(childList => {
                                return childList.isWarn === true;
                            });
                            listItem.status = hasFailure ? "failed" : hasWarning ? "warn" : "success";
                            listItem.children = parsedResponse;
                        } else {
                            failedStep = true;
                        }
                    } else {
                        failedStep = true;
                    }
                } else {
                    failedStep = true;
                }
                if (failedStep) {
                    listItem.status = "failed";
                    listItem.message = parsedResponse?.message ? parsedResponse.message : null;
                }
            } catch (e) {
                console.log("Can't update status in ValidationList", e);
                listItem.status = "failed";
                listItem.message = e.message ? e.message : e;
            }

            setValidationListState(prevstate => {
                prevstate[index] = listItem;
                updateButtonState(prevstate);
                return prevstate;
            });

            updateStatus(list, index + 1);
        };

        const executeValidationListAPI = async () => {
            const validationList = [...validationListState];
            updateStatus(validationList, 0);
        };

        const updateButtonState = validationList => {
            if (cancelRef?.current) return;
            const isAPICompleted = !validationList.some(item => {
                return item.status === "todo";
            });
            const isAPISuccessful = validationList.every(item => {
                return item.status === "done" || item.status === "success" || item.status === "warn";
            });
            const canRetry = isAPICompleted && !isAPISuccessful;

            setAPIStatus({
                isAPICompleted: isAPICompleted,
                isAPISuccessful: isAPISuccessful,
                canRetry: canRetry
            });
        };

        const customCircularProgress = (
            <>
                <CircularProgress
                    variant="determinate"
                    size={24}
                    thickness={8}
                    value={100}
                    sx={{ ...styles.circularProgressBase, ...styles.outerCircularProgress }}
                />
                <CircularProgress disableShrink color="secondary" size={24} thickness={8} />
            </>
        );

        const statusMapping = currentStatus => {
            const icons = {
                todo: customCircularProgress,
                success: <SuccessIcon style={styles.svgIcon} />,
                failed: <ErrorIcon style={styles.svgIcon} />,
                done: <SuccessIcon style={styles.svgIcon} />,
                fail: <ErrorIcon style={styles.svgIcon} />,
                warn: <WarningIcon style={styles.svgIcon} />
            };

            return icons[currentStatus];
        };
        const renderListItem = listItem => {
            return (
                <Grid
                    container
                    sx={{
                        ...styles.gridContainerBase,
                        ...(left ? styles.leftPositionedIcon : styles.rightPositionedIcon)
                    }}
                >
                    <Grid item sx={styles.gridstatus}>
                        {statusMapping(listItem.isWarn ? "warn" : listItem.status)}
                    </Grid>
                    <Grid item sx={styles.gridItemBase}>
                        <EllipsisText text={listItem.text || listItem.name} sx={styles.typography} />
                        {listItem.message &&
                            (listItem.status === "failed" || listItem.status === "fail" || listItem.isWarn) && (
                                <StriimTypography variant="body4" sx={styles.typography}>
                                    {listItem.message}{" "}
                                </StriimTypography>
                            )}
                        {listItem.viewDocumentation && listItem.viewDocumentation}
                        {listItem.helpCommand &&
                            listItem.helpCommand !== "null" &&
                            (listItem.status === "failed" || listItem.status === "fail" || listItem.isWarn) && (
                                <StriimCodeBlock
                                    code={listItem.helpCommand}
                                    copyButton={listItem.instructionType === "SQL"}
                                    isText={listItem.instructionType !== "SQL"}
                                />
                            )}
                        {listItem.children?.map(childItem => renderListItem(childItem))}
                    </Grid>
                </Grid>
            );
        };

        return validationListState?.map(listItem => renderListItem(listItem));
    }
);

export default ValidationList;
